import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { MaskLines } from "@/components/Reveal";
import { HeroStage } from "@/components/HeroStage";

const HERO_BG =
  "https://images.pexels.com/photos/9028761/pexels-photo-9028761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STATS = [
  { v: "8+", l: "Gadi darbnīcā" },
  { v: "3400+", l: "Apkalpoti auto" },
  { v: "24h", l: "Diagnostikas atbilde" },
  { v: "12 mēn.", l: "Garantija darbiem" },
];

export const Hero = ({ content, services }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden grain pb-16 pt-24 sm:pt-28">
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <img src={HERO_BG} alt="" className="h-[120%] w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080a]/75 via-[#08080a]/90 to-[#08080a]" />
      </motion.div>
      <div className="grid-floor absolute inset-0 -z-10 opacity-60" />

      <div className="mx-auto w-full max-w-[1500px] px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.95, duration: 0.8 }}
          className="flex items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.3em] text-rose-500"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-500" />
          {content.hero_overline}
        </motion.div>

        <div className="mt-5 grid items-end gap-6 lg:grid-cols-[1.25fr_1fr]">
          <h1 className="font-head text-[clamp(2.4rem,6.6vw,5.6rem)] font-extrabold uppercase italic leading-[0.86] tracking-tight">
            <MaskLines
              delay={2}
              lines={[
                <span key="1" className="metal-text">{content.hero_title_line1}</span>,
                <span key="2" className="crimson-text">{content.hero_title_line2}</span>,
              ]}
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.35, duration: 0.9 }}
            className="lg:pb-3"
          >
            <p className="max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">{content.hero_subtitle}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#pieteikums"
                data-testid="hero-cta-booking"
                className="group flex items-center gap-3 bg-rose-600 px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-white transition-transform duration-300 hover:-translate-y-0.5 glow-crimson"
              >
                Pieteikt vizīti
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
              </a>
              <a
                href={`tel:${(content.phone || "").replace(/\s/g, "")}`}
                data-testid="hero-cta-phone"
                className="flex items-center gap-3 border border-white/20 px-6 py-3.5 font-head text-base font-bold uppercase tracking-wide text-slate-200 transition-colors hover:border-rose-500 hover:text-white"
              >
                <Phone className="h-4 w-4 text-rose-500" />
                {content.phone}
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10"
        >
          <HeroStage services={services} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.9, duration: 1 }}
          className="mt-20 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.l} className="bg-[#0b0b0f] px-4 py-5">
              <div className="font-head text-2xl font-extrabold text-white sm:text-3xl">{s.v}</div>
              <div className="mt-1 font-mono-t text-[9px] uppercase leading-tight tracking-[0.18em] text-slate-500">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
